import { uiText } from "@/lib/i18n/uiText";
import { useMemo, useState } from "react";
import { Globe, Check, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ENABLED_LOCALES, DEFAULT_LOCALE, getLocale, localizedPath, parsePath } from "@/lib/i18n/locales";
import { groupedLocales } from "@/lib/i18n/localeRegions";
import { useLanguage } from "@/components/i18n/LanguageProvider";
import { cn } from "@/lib/utils";

/**
 * Language selector shown in the header. Lists every enabled locale by its
 * native name and switches the whole site (URL prefix + dictionaries) on
 * selection, preserving the current page.
 *
 * Every dependency is read defensively: a missing language context, a missing
 * translation function or an unknown locale code degrades to English chrome
 * plus a hard navigation instead of throwing and blanking the header.
 */
export function LanguageSwitcher({ className }: { className?: string }) {
  let translate: (key: string) => string;
  try {
    const { t } = useTranslation();
    translate = (key: string) => {
      try {
        const value = t(key);
        return typeof value === "string" && value && value !== key ? value : FALLBACK_LABELS[key] ?? key;
      } catch {
        return FALLBACK_LABELS[key] ?? key;
      }
    };
  } catch {
    translate = (key: string) => FALLBACK_LABELS[key] ?? key;
  }

  const ctx = useLanguage();
  const locale = ctx?.locale ?? DEFAULT_LOCALE;
  const localeDef = ctx?.localeDef ?? getLocale(locale);
  const setLocale =
    ctx?.setLocale ??
    ((code: string) => {
      if (typeof window === "undefined") return;
      const { path } = parsePath(window.location.pathname);
      window.location.assign(localizedPath(code, path) + window.location.search + window.location.hash);
    });

  const [query, setQuery] = useState("");
  const groups = useMemo(() => {
    try {
      const g = groupedLocales(query);
      if (g.length) return g;
    } catch {
      /* fall through to the flat list below */
    }
    const flat = ENABLED_LOCALES?.length ? ENABLED_LOCALES : [getLocale(DEFAULT_LOCALE)];
    return [{ region: "", locales: flat }];
  }, [query]);

  const pick = (code: string, dir?: string) => {
    void dir;
    try {
      setLocale(code);
    } catch {
      if (typeof window !== "undefined") {
        const { path } = parsePath(window.location.pathname);
        window.location.assign(localizedPath(code, path));
      }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn("gap-1.5", className)}
          aria-label={translate("header.selectLanguage")}
        >
          <Globe className="h-4 w-4" />
          <span className="hidden sm:inline uppercase">{localeDef?.code ?? DEFAULT_LOCALE}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[70vh] w-64 overflow-y-auto">
        <DropdownMenuLabel>{translate("header.language")}</DropdownMenuLabel>
        <div className="px-2 pb-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              placeholder={uiText("Search languages")}
              aria-label={uiText("Search languages")}
              className="h-8 pl-7 text-sm"
            />
          </div>
        </div>
        <DropdownMenuSeparator />
        {groups.length === 0 && (
          <p className="px-2 py-3 text-sm text-muted-foreground">{uiText("No languages match “")}{query}”.</p>
        )}
        {groups.map((group) => (
          <div key={group.region || "all"}>
            {group.region && (
              <DropdownMenuLabel className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
                {group.region}
              </DropdownMenuLabel>
            )}
            {group.locales.map((l) => (
              <DropdownMenuItem
                key={l.code}
                onSelect={() => pick(l.code, l.dir)}
                className="flex items-center justify-between gap-2"
                dir={l.dir}
              >
                <span>{l.nativeName ?? l.code}</span>
                {l.code === locale && <Check className="h-4 w-4 text-primary" />}
              </DropdownMenuItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const FALLBACK_LABELS: Record<string, string> = {
  "header.selectLanguage": "Select language",
  "header.language": "Language",
};
